import type { SubscriptionTier } from '@/lib/api/schemas'

/**
 * Tier-specific metallic accent colours and brand labels — direct port of
 * `ui/squadron/components/TierMeta.kt` in the Android source. Used wherever
 * tiers are picked or compared (squadron cards, tier pickers, badges).
 *
 * Don't shorten these into the @theme tokens — they're product taxonomy,
 * not part of the e404 visual language, and live alongside `SubscriptionTier`.
 */
export const TIER_ACCENT: Record<SubscriptionTier, string> = {
  FREE: '#7A8099',
  LEVEL_1: '#CD7F32', // bronze
  LEVEL_2: '#C0C5D6', // silver
  LEVEL_3: '#FFC857', // gold
}

export const TIER_BRAND_LABEL: Record<SubscriptionTier, string> = {
  FREE: 'FREE',
  LEVEL_1: 'BRONZE',
  LEVEL_2: 'SILVER',
  LEVEL_3: 'GOLD',
}

export const TIER_MEMBER_CAP: Record<SubscriptionTier, number> = {
  FREE: 10,
  LEVEL_1: 25,
  LEVEL_2: 50,
  LEVEL_3: 300,
}

export const TIER_ADMIN_CAP: Record<SubscriptionTier, number> = {
  FREE: 1,
  LEVEL_1: 3,
  LEVEL_2: 5,
  LEVEL_3: 10,
}

// Prices mirror Android `SubscriptionTier` (Models.kt:31-40) — the backend
// resolves the actual Stripe price ID from { targetTier, billingInterval },
// so these are display-only.
export const TIER_MONTHLY_PRICE: Record<SubscriptionTier, number> = {
  FREE: 0,
  LEVEL_1: 15,
  LEVEL_2: 35,
  LEVEL_3: 75,
}

export const TIER_ANNUAL_PRICE: Record<SubscriptionTier, number> = {
  FREE: 0,
  LEVEL_1: 150,
  LEVEL_2: 350,
  LEVEL_3: 750,
}

/** Percentage saved by paying annually vs 12 monthly payments. Truncates
 *  like Kotlin's `.toInt()` (Models.kt:42-45) so both apps show the same
 *  number (16%, not 17%). */
export function annualSavingPercent(tier: SubscriptionTier): number {
  const monthly = TIER_MONTHLY_PRICE[tier]
  if (monthly === 0) return 0
  return Math.trunc((1 - TIER_ANNUAL_PRICE[tier] / (monthly * 12)) * 100)
}

/**
 * Resolve a tier from any string — handles the legacy / unexpected values
 * the backend sometimes returns (e.g. `null`, `undefined`, `LEVEL_1 `).
 * Falls back to FREE.
 */
export function resolveTier(tier: string | null | undefined): SubscriptionTier {
  if (!tier) return 'FREE'
  const v = tier.toUpperCase().trim()
  if (v === 'FREE' || v === 'LEVEL_1' || v === 'LEVEL_2' || v === 'LEVEL_3') {
    return v
  }
  return 'FREE'
}
